import { useEffect, useRef } from "react";
import { useAnalysisDataStore, useAnalysisStore } from "@/features/analysis";
import { fetchGames } from "./games.api";
import { useGamesStore } from "./games.store";
import { importRunner } from "./import-runner";

export const BACKGROUND_IMPORT_INTERVAL = 30 * 60 * 1000;

/** Imports games played since the newest one in the library; resolves to how many were imported. */
export const checkBackgroundImport = async (userId: string, signal: AbortSignal, now = new Date()): Promise<number> => {
  let imported = 0;
  const current = () => !signal.aborted && useGamesStore.getState().ownerUserId === userId;
  const available = () => current() && !importRunner.running && !useAnalysisStore.getState().isRunning;
  if (!available() || !navigator.locks) return imported;
  try {
    await navigator.locks.request(`chessla-background-import:${userId}`, { ifAvailable: true }, async (lock) => {
      if (!lock || !available()) return;
      const key = `chessla-background-import:${userId}:checked`;
      const lastCheck = Number(localStorage.getItem(key));
      if (lastCheck > 0 && now.getTime() - lastCheck < BACKGROUND_IMPORT_INTERVAL) return;
      localStorage.setItem(key, String(now.getTime()));
      const [latest] = await fetchGames(1, 0, { sortBy: "playedAt", sortDirection: "desc" }, signal);
      if (!latest || !available()) return;
      const fromKey = `chessla-background-import:${userId}:from`;
      const pendingFrom = Date.parse(localStorage.getItem(fromKey) ?? "");
      const latestFrom = Date.parse(latest.playedAt);
      const from = Number.isFinite(pendingFrom) ? Math.min(pendingFrom, latestFrom) : latestFrom;
      if (!Number.isFinite(from) || from >= now.getTime()) return;
      localStorage.setItem(fromKey, new Date(from).toISOString());
      const result = await importRunner.start(
        "all",
        { from: new Date(from).toISOString(), to: now.toISOString() },
        { quiet: true },
      );
      if (!current() || result.phase !== "complete") return;
      localStorage.removeItem(fromKey);
      if (result.imported === 0) return;
      imported = result.imported;
      useGamesStore.getState().invalidateLibrary();
      await Promise.all([
        useGamesStore.getState().refreshGames(),
        ...(useAnalysisDataStore.getState().ownerUserId === userId
          ? [useAnalysisDataStore.getState().refreshUnanalyzed()]
          : []),
      ]);
    });
  } catch {
    // Background failures stay silent; manual import remains available.
  }
  return imported;
};

export const useBackgroundImport = (userId: string | null, onImported?: (count: number) => void) => {
  const notify = useRef(onImported);
  notify.current = onImported;
  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();
    const check = () => {
      if (document.visibilityState === "visible")
        void checkBackgroundImport(userId, controller.signal).then((count) => {
          if (count > 0 && !controller.signal.aborted) notify.current?.(count);
        });
    };
    check();
    const timer = window.setInterval(check, BACKGROUND_IMPORT_INTERVAL);
    document.addEventListener("visibilitychange", check);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
    };
  }, [userId]);
};
